import { useCallback, useState } from "react";
import { toast } from "sonner";
import { fetchFastApi, ApiError } from "../api/fastapi";
import { useIntegrations } from "./useIntegrations";
import { OptroConnectionConfig } from "../models/audit/optroSync";

// Whether this workspace can reach Optro: a real, enabled Optro
// integration is required. Pass `enabled: false` to skip the integrations
// fetch entirely (e.g. when the surface consuming this is feature-flagged
// off) — hooks can't be called conditionally, so the gate lives here.
export function useOptroConnection(enabled: boolean = true) {
  const { integrations, loading } = useIntegrations({ fetchOnLoad: enabled });

  const connected = integrations.some(
    (integration) =>
      integration.integrationType === "auditboard" && integration.enabled,
  );

  return { connected, loading };
}

// Creates the workspace's Optro integration. Uses the same endpoint and
// body shape as the admin AuditBoardConfigForm's create call.
export function useConnectOptro() {
  const [connecting, setConnecting] = useState(false);

  const connect = useCallback(
    async (config: OptroConnectionConfig): Promise<boolean> => {
      setConnecting(true);
      try {
        await fetchFastApi({
          method: "POST",
          path: "/workspace/integrations/auditboard",
          body: {
            base_url: config.baseUrl,
            default_workspace_id: config.defaultWorkspaceId,
            api_key: config.apiKey,
          },
        });
        return true;
      } catch (error) {
        console.error("Optro connect failed:", error);
        if (error instanceof ApiError) {
          toast.error(error.getUserMessage());
        } else {
          toast.error(
            "Could not connect to Optro. Check the connection details.",
          );
        }
        return false;
      } finally {
        setConnecting(false);
      }
    },
    [],
  );

  return { connect, connecting };
}
